import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import radiologyResultService from '../../services/radiologyResultService';
import RadiologyConfigDashboard from '../../components/radiology/RadiologyConfigDashboard';
import RadiologySmartPhraseMaster from '../../components/radiology/RadiologySmartPhraseMaster';
import RadiologyTemplateEditorView from '../../components/radiology/RadiologyTemplateEditorView';
import RadiologyTemplatePreviewModal from '../../components/radiology/RadiologyTemplatePreviewModal';
import RadiologyVersionHistoryDrawer from '../../components/radiology/RadiologyVersionHistoryDrawer';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import './RadiologyTemplatesMasterPage.css';

/**
 * Radiology Master Configuration & Template Management System
 * Comprehensive, enterprise-grade platform for Modality, Investigation,
 * Template, Macro Smart Phrases, Version Control, and Governance.
 */
export default function RadiologyTemplatesMasterPage() {
  const navigate = useNavigate();

  // Top-Level Navigation Tabs: 'DASHBOARD' | 'TEMPLATES' | 'SMART_PHRASES'
  const [activeTab, setActiveTab] = useState('TEMPLATES');

  // View Mode inside Templates Tab: 'CATALOG' | 'EDITOR'
  const [viewMode, setViewMode] = useState('CATALOG');
  const [activeEditorTemplateId, setActiveEditorTemplateId] = useState(null); // null = new

  // 1. Department & Test Catalog State
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [selectedDeptId, setSelectedDeptId] = useState('ALL'); // 'ALL' or departmentId number
  const [testSearch, setTestSearch] = useState('');
  const [selectedTest, setSelectedTest] = useState(null);

  // 2. Templates for Selected Test
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // 3. Modals: Preview & History
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [historyTemplate, setHistoryTemplate] = useState(null);

  // 4. DOCX Batch Import Refs & States
  const singleDocxInputRef = useRef(null);
  const multiDocxInputRef = useRef(null);
  const [importingSingleDocx, setImportingSingleDocx] = useState(false);
  const [multiImporting, setMultiImporting] = useState(false);
  const [, setMultiImportProgress] = useState(null); // { current, total, fileName }

  // 5. Delete Confirmation Modal
  const [deleteTarget, setDeleteTarget] = useState(null); // { templateId, title }

  // Clear notifications after 5s
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // Load Department Catalog on Mount
  const fetchCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const res = await radiologyResultService.getDepartmentCatalog();
      if (res.success && Array.isArray(res.data)) {
        setCatalog(res.data);

        // Auto-select first available test if none selected
        if (!selectedTest) {
          for (const dept of res.data) {
            if (dept.tests && dept.tests.length > 0) {
              setSelectedDeptId(dept.departmentId);
              setSelectedTest(dept.tests[0]);
              break;
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch department catalog:', err);
      setErrorMsg('Failed to load radiology departments and investigations catalog.');
    } finally {
      setLoadingCatalog(false);
    }
  }, [selectedTest]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  // Fetch Templates for the Active Selected Test
  const fetchTemplatesForActiveTest = useCallback(async (invId) => {
    if (!invId) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    setErrorMsg(null);
    try {
      const res = await radiologyResultService.getTemplatesByInvestigation(invId);
      if (res.success && Array.isArray(res.data)) {
        setTemplates(res.data);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
      setErrorMsg('Could not load templates for this investigation.');
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTest?.investigationId) {
      fetchTemplatesForActiveTest(selectedTest.investigationId);
    }
  }, [selectedTest, fetchTemplatesForActiveTest]);

  // Handle Changing Modality / Department Tab
  const handleSelectModality = (deptId) => {
    setSelectedDeptId(deptId);
    if (deptId !== 'ALL') {
      const dept = catalog.find((d) => d.departmentId === deptId);
      if (dept && dept.tests && dept.tests.length > 0) {
        setSelectedTest(dept.tests[0]);
      }
    }
  };

  // Filtered Investigations list based on selected modality and testSearch
  const filteredTests = React.useMemo(() => {
    let list = [];
    if (selectedDeptId === 'ALL') {
      list = catalog.flatMap((d) =>
        (d.tests || []).map((t) => ({ ...t, departmentName: d.departmentName }))
      );
    } else {
      const dept = catalog.find((d) => d.departmentId === selectedDeptId);
      list = (dept?.tests || []).map((t) => ({ ...t, departmentName: dept.departmentName }));
    }

    if (!testSearch.trim()) return list;

    const q = testSearch.toLowerCase();
    return list.filter(
      (t) =>
        (t.investigationName || '').toLowerCase().includes(q) ||
        (t.testCode || '').toLowerCase().includes(q) ||
        (t.departmentName || '').toLowerCase().includes(q)
    );
  }, [catalog, selectedDeptId, testSearch]);

  // Filtered Templates list based on templateSearch
  const filteredTemplates = React.useMemo(() => {
    if (!templateSearch.trim()) return templates;
    const q = templateSearch.toLowerCase();
    return templates.filter(
      (t) =>
        (t.title || t.templateTitle || '').toLowerCase().includes(q) ||
        (t.templateDesc || '').toLowerCase().includes(q) ||
        (t.doctorName || '').toLowerCase().includes(q)
    );
  }, [templates, templateSearch]);

  // Centralized Pagination for templates
  const templatePagination = usePagination({
    items: filteredTemplates,
    resetDeps: [selectedTest, templateSearch],
  });

  // Quick 1-Click Set as Default Template
  const handleSetDefault = async (tmpl) => {
    if (!selectedTest?.investigationId) return;
    try {
      const res = await radiologyResultService.setDefaultTemplate(
        tmpl.templateId,
        selectedTest.investigationId
      );
      if (res.success) {
        setSuccessMsg(`"${tmpl.title || tmpl.templateTitle}" is now the active default template.`);
        fetchTemplatesForActiveTest(selectedTest.investigationId);
        fetchCatalog();
      }
    } catch (err) {
      console.error('Failed to set default template:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to update default template.');
    }
  };

  // Open Full Clinical Template Editor
  const handleOpenEditor = (templateId = null) => {
    if (!selectedTest) {
      setErrorMsg('Please select an investigation first.');
      return;
    }
    setActiveEditorTemplateId(templateId);
    setViewMode('EDITOR');
  };

  // Clone / Duplicate Template
  const handleDuplicate = async (templateId, templateTitle) => {
    try {
      const res = await radiologyResultService.duplicateTemplate(templateId);
      if (res.success) {
        setSuccessMsg(`Cloned "${templateTitle}" as a new variant.`);
        fetchTemplatesForActiveTest(selectedTest.investigationId);
        fetchCatalog();
      }
    } catch (err) {
      console.error('Failed to duplicate template:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to clone template.');
    }
  };

  // Delete / Deactivate Template — uses inline modal instead of window.confirm
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { templateId, title } = deleteTarget;
    setDeleteTarget(null);
    try {
      const res = await radiologyResultService.deleteTemplate(templateId);
      if (res.success) {
        setSuccessMsg(`Template "${title}" has been deactivated and removed from active library.`);
        fetchTemplatesForActiveTest(selectedTest.investigationId);
        fetchCatalog();
      }
    } catch (err) {
      console.error('Failed to deactivate template:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to deactivate template.');
    }
  };

  // Single DOCX Import in Catalog
  const handleSingleDocxChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTest) return;
    setImportingSingleDocx(true);
    setErrorMsg(null);
    try {
      const res = await radiologyResultService.extractDocx(file);
      if (res.success && res.data?.extractedHtml) {
        // Create template directly
        const title = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        const payload = {
          investigationId: selectedTest.investigationId,
          title: title,
          templateTitle: title,
          templateDesc: res.data.extractedHtml,
          gender: 'B',
          isDefault: templates.length === 0,
          scope: 'SHARED',
          isActive: true
        };
        const createRes = await radiologyResultService.createTemplate(payload);
        if (createRes.success) {
          setSuccessMsg(`Imported Word document "${file.name}" as new template.`);
          fetchTemplatesForActiveTest(selectedTest.investigationId);
          fetchCatalog();
        }
      }
    } catch (err) {
      console.error('Failed to import DOCX:', err);
      setErrorMsg('Failed to extract Word document content.');
    } finally {
      setImportingSingleDocx(false);
      e.target.value = '';
    }
  };

  // Multi-DOCX Batch Import
  // NOTE: Do NOT manually set Content-Type header — axios must auto-set multipart boundary
  const handleMultiDocxChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const inputEl = e.target;
    if (!files.length || !selectedTest) {
      inputEl.value = '';
      return;
    }

    setMultiImporting(true);
    setMultiImportProgress({ current: 0, total: files.length });
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('investigationId', selectedTest.investigationId);

      // Let axios set Content-Type with correct multipart boundary automatically
      const res = await radiologyResultService.bulkImportDocxFiles(
        files,
        selectedTest.investigationId
      );

      if (res.success && res.data) {
        const { successCount, failureCount, totalProcessed } = res.data;
        if (failureCount > 0) {
          setSuccessMsg(
            `Imported ${successCount} of ${totalProcessed} templates. ⚠️ ${failureCount} file(s) failed — check that they are valid Word (.docx) documents.`
          );
        } else {
          setSuccessMsg(
            `✓ All ${successCount} Word template(s) imported successfully for "${selectedTest.investigationName}"!`
          );
        }
        fetchTemplatesForActiveTest(selectedTest.investigationId);
        fetchCatalog();
      } else {
        setErrorMsg('Import completed but no data was returned. Please check the files and try again.');
      }
    } catch (err) {
      console.error('Multi DOCX import failed:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      setErrorMsg(`Word batch import failed: ${msg || 'Unknown error. Ensure files are valid .docx format.'}`);
    } finally {
      setMultiImporting(false);
      setMultiImportProgress(null);
      inputEl.value = '';
    }
  };

  // Export Template to DOCX
  const handleExportDocx = async (templateId, title) => {
    try {
      const blob = await radiologyResultService.exportTemplateDocx(templateId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(title || 'Template').replace(/\s+/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Failed to export DOCX:', err);
      setErrorMsg('Failed to download Word template.');
    }
  };

  // Modality helper icon
  const getModalityIcon = (name = '') => {
    const upper = name.toUpperCase();
    if (upper.includes('X-RAY')) return '☢️';
    if (upper.includes('ULTRA') || upper.includes('USG')) return '📡';
    if (upper.includes('CT')) return '🔬';
    if (upper.includes('MRI')) return '🧲';
    if (upper.includes('MAMMO')) return '🎀';
    if (upper.includes('PET')) return '⚛️';
    return '📑';
  };

  // Strip HTML tags for clean card preview
  const stripHtml = (html = '') => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="rad-tmpl-page">
      {/* Hidden File Inputs */}
      <input
        type="file"
        accept=".docx"
        ref={singleDocxInputRef}
        style={{ display: 'none' }}
        onChange={handleSingleDocxChange}
      />
      <input
        type="file"
        accept=".docx"
        multiple
        ref={multiDocxInputRef}
        style={{ display: 'none' }}
        onChange={handleMultiDocxChange}
      />

      {/* Top Header & Breadcrumb */}
      <header className="rad-tmpl-topbar">
        <div className="rad-tmpl-title-area">
          <div className="rad-tmpl-breadcrumb">
            <button onClick={() => navigate('/radiology-results')}>Radiology Results</button>
            <span>/</span>
            <strong>Master Configuration & Templates</strong>
          </div>
          <h1>⚙️ Radiology Master Configuration & Templates</h1>
          <p className="rad-tmpl-subtitle">
            Configure modalities, investigations, diagnostic templates, smart phrases, dynamic fields, and governance rules.
          </p>
        </div>

        <div className="rad-tmpl-top-actions">
          <button
            type="button"
            className="rad-tmpl-btn-workbench"
            onClick={() => navigate('/radiology-results')}
          >
            ☢️ Open Reporting Workbench
          </button>
        </div>
      </header>

      {/* Global Alerts */}
      {errorMsg && (
        <div className="rad-tmpl-alert rad-tmpl-alert-error">
          <span>⚠️ {errorMsg}</span>
          <button onClick={() => setErrorMsg(null)}>✕</button>
        </div>
      )}
      {successMsg && (
        <div className="rad-tmpl-alert rad-tmpl-alert-success">
          <span>✓ {successMsg}</span>
          <button onClick={() => setSuccessMsg(null)}>✕</button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TOP NAVIGATION TABS: DASHBOARD | TEMPLATES | SMART PHRASES               */}
      {/* ========================================================================= */}
      {viewMode === 'CATALOG' && (
        <nav className="rad-main-nav-tabs">
          <button
            type="button"
            className={`rad-nav-tab ${activeTab === 'DASHBOARD' ? 'active' : ''}`}
            onClick={() => setActiveTab('DASHBOARD')}
          >
            📊 Configuration Dashboard
          </button>
          <button
            type="button"
            className={`rad-nav-tab ${activeTab === 'TEMPLATES' ? 'active' : ''}`}
            onClick={() => setActiveTab('TEMPLATES')}
          >
            📑 Templates & Library
          </button>
          <button
            type="button"
            className={`rad-nav-tab ${activeTab === 'SMART_PHRASES' ? 'active' : ''}`}
            onClick={() => setActiveTab('SMART_PHRASES')}
          >
            💬 Smart Phrases Master
          </button>
        </nav>
      )}

      {/* ========================================================================= */}
      {/* TAB 1: CONFIGURATION DASHBOARD                                            */}
      {/* ========================================================================= */}
      {viewMode === 'CATALOG' && activeTab === 'DASHBOARD' && (
        <RadiologyConfigDashboard
          onSelectInvestigation={(inv) => {
            setSelectedDeptId(inv.departmentId || 'ALL');
            setSelectedTest(inv);
            setActiveTab('TEMPLATES');
          }}
          onNavigateTab={(tab) => setActiveTab(tab)}
        />
      )}

      {/* ========================================================================= */}
      {/* TAB 2: SMART PHRASES MASTER                                               */}
      {/* ========================================================================= */}
      {viewMode === 'CATALOG' && activeTab === 'SMART_PHRASES' && (
        <RadiologySmartPhraseMaster />
      )}

      {/* ========================================================================= */}
      {/* VIEW MODE: DEDICATED FULL CLINICAL TEMPLATE EDITOR                        */}
      {/* ========================================================================= */}
      {viewMode === 'EDITOR' && (
        <RadiologyTemplateEditorView
          templateId={activeEditorTemplateId}
          investigation={selectedTest}
          onBack={() => setViewMode('CATALOG')}
          onSaveSuccess={() => {
            fetchTemplatesForActiveTest(selectedTest?.investigationId);
            fetchCatalog();
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* TAB 3 / CATALOG MODE: INVESTIGATIONS & TEMPLATES WORKSPACE                */}
      {/* ========================================================================= */}
      {viewMode === 'CATALOG' && activeTab === 'TEMPLATES' && (
        <div className="rad-tmpl-workspace-wrapper">
          {/* STEP 1: MODALITY / DEPARTMENT SELECTOR TABS */}
          <section className="rad-tmpl-step-section">
            <div className="rad-step-header">
              <span className="rad-step-number">1</span>
              <span className="rad-step-title">Select Modality / Department:</span>
            </div>

            <div className="rad-modality-tabs">
              <button
                type="button"
                className={`rad-modality-tab${selectedDeptId === 'ALL' ? ' active' : ''}`}
                onClick={() => handleSelectModality('ALL')}
              >
                <span className="rad-modality-icon">🌐</span>
                <span className="rad-modality-name">All Modalities</span>
                <span className="rad-modality-badge">
                  {catalog.reduce((acc, d) => acc + (d.totalTemplateCount || 0), 0)}
                </span>
              </button>

              {catalog.map((dept) => (
                <button
                  key={dept.departmentId}
                  type="button"
                  className={`rad-modality-tab${selectedDeptId === dept.departmentId ? ' active' : ''}`}
                  onClick={() => handleSelectModality(dept.departmentId)}
                >
                  <span className="rad-modality-icon">{getModalityIcon(dept.departmentName)}</span>
                  <span className="rad-modality-name">{dept.departmentName}</span>
                  <span className="rad-modality-badge">
                    {dept.totalTemplateCount || 0}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* 2-COLUMN WORKSPACE: LEFT = TESTS LIST, RIGHT = MAPPED TEMPLATES */}
          <div className="rad-tmpl-workspace-grid">
            {/* COLUMN 1: TESTS LIST */}
            <aside className="rad-tests-column">
              <div className="rad-step-header small">
                <span className="rad-step-number">2</span>
                <span className="rad-step-title">Select Investigation:</span>
              </div>

              <div className="rad-tests-search-box">
                <span className="rad-search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search investigations by name or code..."
                  value={testSearch}
                  onChange={(e) => setTestSearch(e.target.value)}
                />
                {testSearch && (
                  <button className="rad-clear-btn" onClick={() => setTestSearch('')}>
                    ✕
                  </button>
                )}
              </div>

              <div className="rad-tests-list">
                {loadingCatalog ? (
                  <div className="rad-loading-box">Loading investigations...</div>
                ) : filteredTests.length === 0 ? (
                  <div className="rad-empty-tests">No investigations found.</div>
                ) : (
                  filteredTests.map((test) => {
                    const isSelected = selectedTest?.investigationId === test.investigationId;
                    return (
                      <div
                        key={test.investigationId}
                        className={`rad-test-item${isSelected ? ' active' : ''}`}
                        onClick={() => setSelectedTest(test)}
                      >
                        <div className="rad-test-item-top">
                          <span className="rad-test-code">{test.testCode || 'INV'}</span>
                          <span
                            className={`rad-test-count-badge${
                              test.templateCount > 0 ? ' has-templates' : ' no-templates'
                            }`}
                          >
                            {test.templateCount} {test.templateCount === 1 ? 'template' : 'templates'}
                          </span>
                        </div>
                        <div className="rad-test-name">{test.investigationName}</div>
                        <div className="rad-test-dept">{test.departmentName}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </aside>

            {/* COLUMN 2: MAPPED TEMPLATES WORKSPACE */}
            <main className="rad-templates-column">
              {selectedTest ? (
                <>
                  {/* Header for the selected test */}
                  <div className="rad-test-detail-header">
                    <div className="rad-tdh-left">
                      <div className="rad-step-header small">
                        <span className="rad-step-number">3</span>
                        <span className="rad-step-title">Diagnostic Templates for:</span>
                      </div>
                      <h2>{selectedTest.investigationName}</h2>
                      <div className="rad-tdh-meta">
                        <span className="rad-meta-pill">Modality: {selectedTest.departmentName}</span>
                        <span className="rad-meta-pill">Code: {selectedTest.testCode}</span>
                        <span className="rad-meta-pill count">
                          Total: {templates.length} {templates.length === 1 ? 'Template' : 'Templates'}
                        </span>
                      </div>
                    </div>

                    {/* Primary Action Buttons */}
                    <div className="rad-tdh-actions">
                      <button
                        type="button"
                        className="rad-action-btn primary"
                        onClick={() => handleOpenEditor(null)}
                      >
                        + Create New Template
                      </button>

                      <button
                        type="button"
                        className="rad-action-btn secondary"
                        disabled={importingSingleDocx}
                        onClick={() => singleDocxInputRef.current?.click()}
                        title="Upload single DOCX into a new template"
                      >
                        📥 {importingSingleDocx ? 'Importing...' : 'Import Word (.docx)'}
                      </button>

                      <button
                        type="button"
                        className="rad-action-btn multi"
                        disabled={multiImporting}
                        onClick={() => multiDocxInputRef.current?.click()}
                        title="Upload multiple Word (.docx) files at once"
                      >
                        {multiImporting ? 'Importing...' : '📦 Multiple Word Import'}
                      </button>
                    </div>
                  </div>

                  {/* Template search bar within this test */}
                  {templates.length > 0 && (
                    <div className="rad-tmpl-filter-bar">
                      <div className="rad-tmpl-search-field">
                        <span>🔍</span>
                        <input
                          type="text"
                          placeholder="Filter templates by name or keyword..."
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                        />
                        {templateSearch && (
                          <button onClick={() => setTemplateSearch('')}>✕</button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Templates List / Grid */}
                  <div className="rad-templates-container">
                    {loadingTemplates ? (
                      <div className="rad-loading-box">
                        Loading templates for {selectedTest.investigationName}...
                      </div>
                    ) : templatePagination.totalRecords === 0 ? (
                      <div className="rad-empty-state-card">
                        <div className="rad-empty-icon">📑</div>
                        <h3>No Templates Configured for {selectedTest.investigationName}</h3>
                        <p>
                          Radiologists and technicians require standardized diagnostic templates for clinical reporting.
                          You can create a new baseline template or import your existing hospital Word (.docx) files.
                        </p>
                        <div className="rad-empty-actions">
                          <button
                            type="button"
                            className="rad-action-btn primary"
                            onClick={() => handleOpenEditor(null)}
                          >
                            + Create First Template
                          </button>
                          <button
                            type="button"
                            className="rad-action-btn secondary"
                            onClick={() => singleDocxInputRef.current?.click()}
                          >
                            📥 Import Word (.docx)
                          </button>
                          <button
                            type="button"
                            className="rad-action-btn multi"
                            onClick={() => multiDocxInputRef.current?.click()}
                          >
                            📦 Multiple Word (.docx) Batch Import
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="rad-templates-grid">
                          {templatePagination.paginatedItems.map((tmpl) => {
                            const isDefault = !!tmpl.isDefault;
                            const title = tmpl.title || tmpl.templateTitle;
                            const previewText = stripHtml(tmpl.templateDesc);

                            return (
                              <div
                                key={tmpl.templateId}
                                className={`rad-tmpl-card${isDefault ? ' is-default-card' : ''}`}
                              >
                                <div className="rad-card-header">
                                  <div className="rad-card-title-wrap">
                                    <h4
                                      className="rad-card-title clickable"
                                      title={`Click to edit: ${title}`}
                                      onClick={() => handleOpenEditor(tmpl.templateId)}
                                    >
                                      {title}
                                    </h4>
                                    <div className="rad-card-badges">
                                      {isDefault ? (
                                        <span className="rad-badge-default">⭐ DEFAULT TEMPLATE</span>
                                      ) : (
                                        <button
                                          type="button"
                                          className="rad-btn-make-default"
                                          onClick={() => handleSetDefault(tmpl)}
                                          title="Click to make this the primary auto-load template for this test"
                                        >
                                          Set as Default
                                        </button>
                                      )}
                                      <span className="rad-badge-scope">{tmpl.scope || 'SHARED'}</span>
                                      <span className="rad-badge-v">v{tmpl.version || 1}</span>
                                      {tmpl.doctorName && (
                                        <span className="rad-badge-doctor">Dr. {tmpl.doctorName}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div
                                  className="rad-card-body"
                                  onClick={() => handleOpenEditor(tmpl.templateId)}
                                  title="Click to open editor"
                                >
                                  <div className="rad-card-preview">
                                    {previewText.length > 220
                                      ? `${previewText.slice(0, 220)}...`
                                      : previewText}
                                  </div>
                                </div>

                                <div className="rad-card-footer">
                                  <button
                                    type="button"
                                    className="rad-card-btn edit"
                                    onClick={() => handleOpenEditor(tmpl.templateId)}
                                  >
                                    ✏️ Open & Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="rad-card-btn preview"
                                    onClick={() => setPreviewTemplate(tmpl)}
                                  >
                                    👁️ Preview
                                  </button>
                                  <button
                                    type="button"
                                    className="rad-card-btn history"
                                    onClick={() => setHistoryTemplate(tmpl)}
                                    title="View version audit history"
                                  >
                                    ⏱️ v{tmpl.version || 1}
                                  </button>
                                  <button
                                    type="button"
                                    className="rad-card-btn clone"
                                    onClick={() => handleDuplicate(tmpl.templateId, title)}
                                    title="Create a variant copy of this template"
                                  >
                                    📋 Clone
                                  </button>
                                  <button
                                    type="button"
                                    className="rad-card-btn export"
                                    onClick={() => handleExportDocx(tmpl.templateId, title)}
                                    title="Download as Word DOCX file"
                                  >
                                    💾 Export
                                  </button>
                                  <button
                                    type="button"
                                    className="rad-card-btn delete"
                                    onClick={() => setDeleteTarget({ templateId: tmpl.templateId, title })}
                                    title="Soft-delete / deactivate this template (it will no longer appear in reporting)"
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Centralized Pagination */}
                        <CommonPagination
                          page={templatePagination.page}
                          pageSize={templatePagination.pageSize}
                          totalRecords={templatePagination.totalRecords}
                          onPageChange={templatePagination.setPage}
                          onPageSizeChange={templatePagination.setPageSize}
                          loading={loadingTemplates}
                        />
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="rad-no-selection-box">
                  <h3>Please select an investigation from the left column</h3>
                </div>
              )}
            </main>
          </div>
        </div>
      )}

      {/* Global Preview Modal */}
      {previewTemplate && (
        <RadiologyTemplatePreviewModal
          template={previewTemplate}
          investigationName={selectedTest?.investigationName}
          departmentName={selectedTest?.departmentName}
          onClose={() => setPreviewTemplate(null)}
        />
      )}

      {/* Global Version History Drawer */}
      {historyTemplate && (
        <RadiologyVersionHistoryDrawer
          templateId={historyTemplate.templateId}
          templateTitle={historyTemplate.title || historyTemplate.templateTitle}
          onClose={() => setHistoryTemplate(null)}
          onRestoreSuccess={() => {
            fetchTemplatesForActiveTest(selectedTest?.investigationId);
            fetchCatalog();
          }}
        />
      )}

      {/* ── Soft-Delete Confirmation Modal ─────────────────────────────── */}
      {deleteTarget && (
        <div className="rad-delete-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="rad-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rad-delete-modal-icon">🗑️</div>
            <h3 className="rad-delete-modal-title">Deactivate Template?</h3>
            <p className="rad-delete-modal-name">&ldquo;{deleteTarget.title}&rdquo;</p>
            <p className="rad-delete-modal-desc">
              This template will be <strong>soft-deleted</strong> (deactivated). It will no longer
              appear in the reporting workbench or template library. Existing finalized patient reports
              that used this template will <strong>not</strong> be affected.
            </p>
            <div className="rad-delete-modal-actions">
              <button
                className="rad-delete-cancel-btn"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                className="rad-delete-confirm-btn"
                onClick={handleDeleteConfirm}
              >
                Yes, Deactivate Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Multi-Import Progress Banner ──────────────────────────────── */}
      {multiImporting && (
        <div className="rad-multi-import-banner">
          <div className="rad-multi-import-spinner" />
          <span>Importing Word documents... please wait</span>
        </div>
      )}
    </div>
  );
}
